"use client";

import { useState } from "react";
import {
  Info,
  CreditCard,
  PoundSterling,
  DollarSign,
  Euro,
  Calendar,
  Calculator,
  Star,
} from "lucide-react";
import { quoteThemeColorStyles, quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { iHolidayQuotePricingOptionData } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { formatSafeQuoteDate } from "@/components/quotes/helpers/format-safe-date";

export interface PricingSectionProps extends iHolidayQuotePricingOptionData {
  primaryColor?: string;
}

const CURRENCY_SYMBOLS = {
  GBP: { symbol: "£", icon: PoundSterling },
  USD: { symbol: "$", icon: DollarSign },
  EUR: { symbol: "€", icon: Euro },
  INR: { symbol: "₹", icon: "₹" },
  LKR: { symbol: "Rs.", icon: "Rs." },
};

export function PricingSection({
  name,
  description,
  totalPrice,
  breakdown,
  currency = "GBP",
  depositRequired,
  depositDueDate,
  balanceDueDate,
  discount,
  discountName,
  interestFreeInstallmentMonths,
  showAsPricePerPerson,
}: PricingSectionProps) {
  const CurrencyIcon = CURRENCY_SYMBOLS[currency].icon;
  const currencySymbol = CURRENCY_SYMBOLS[currency].symbol;

  const originalPrice = discount ? totalPrice + discount : totalPrice;
  const installmentAmount =
    interestFreeInstallmentMonths && depositRequired
      ? (totalPrice - depositRequired) / interestFreeInstallmentMonths
      : 0;

  // If no breakdown, use a single column layout
  const hasBreakdown = breakdown && breakdown.length > 0;

  return (
    <section
      className="@container bg-white rounded-xl shadow-2xl border-2 p-4 sm:p-6 relative overflow-hidden hover:shadow-3xl transition-all duration-300 hover:scale-[1.02]"
      style={quoteThemeColorStyles.accentBorder}
    >
      {/* Highlight Badge */}
      <div
        className="absolute top-0 right-0 px-6 py-1.5 rounded-bl-2xl text-xs font-semibold flex items-center gap-1"
        style={quoteThemeColorStyles.primaryButton}
      >
        <Star className="w-3 h-3" />
        Pricing
      </div>

      {/* Header */}
      <div className="mb-4 sm:mb-6 relative z-10">
        {name && (
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span
              className="w-1 h-6 rounded-full"
              style={quoteThemeColorStyles.primaryGradientBar}
            ></span>
            {name}
          </h2>
        )}
        {description && (
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            {description}
          </p>
        )}
      </div>

      <div
        className={
          hasBreakdown
            ? "grid gap-4 sm:gap-8 @[640px]:grid-cols-2"
            : "space-y-4 sm:space-y-8"
        }
      >
        {/* Price Details */}
        <div className="space-y-3 sm:space-y-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Price Details
          </h3>

          {/* Breakdown if available */}
          {hasBreakdown && (
            <div className="space-y-2 sm:space-y-3">
              {breakdown.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start justify-between p-2 sm:p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-start gap-1.5 sm:gap-2">
                    <span className="text-sm text-gray-700">
                      {item.description}
                    </span>
                    {item.info && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info
                            className="w-3.5 h-3.5 sm:w-4 sm:h-4 cursor-help flex-shrink-0"
                            style={quoteThemeColorStyles.primary}
                          />
                        </TooltipTrigger>
                        <TooltipContent>{item.info}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <span className="text-sm font-medium">
                    {currencySymbol}
                    {(item.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Total Price Section - Made larger when no breakdown */}
          <div
            className={`p-4 sm:p-6 rounded-xl space-y-2 sm:space-y-3 relative ${
              !hasBreakdown ? "text-center" : ""
            }`}
            style={quoteThemeColorStyles.accentBadge}
          >
            {/* Glow effect */}
            <div
              className="absolute inset-0 rounded-xl"
              style={quoteThemeColorStyles.softGradientSurface}
            ></div>
            <div className="relative z-10">
              {discount ? (
                <>
                  <div
                    className={`flex items-center ${
                      !hasBreakdown ? "justify-center" : "justify-between"
                    }`}
                  >
                    <span className="font-medium text-sm sm:text-base">
                      Original Price
                      {showAsPricePerPerson ? " (per person)" : ""}
                    </span>
                    <span
                      className={`line-through text-sm sm:text-base ${
                        !hasBreakdown
                          ? "text-lg sm:text-xl ml-2 sm:ml-3"
                          : "text-base sm:text-lg"
                      }`}
                    >
                      {currencySymbol}
                      {(originalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`flex items-center ${
                      !hasBreakdown ? "justify-center" : "justify-between"
                    }`}
                  >
                    <span className="font-medium text-sm sm:text-base">
                      {discountName || "Discount"}
                      {showAsPricePerPerson ? " (per person)" : ""}
                    </span>
                    <span
                      className={`text-sm sm:text-base ${
                        !hasBreakdown
                          ? "text-lg sm:text-xl ml-2 sm:ml-3"
                          : "text-base sm:text-lg"
                      }`}
                    >
                      -{currencySymbol}
                      {(discount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div
                    className={`flex items-center ${
                      !hasBreakdown ? "justify-center" : "justify-between"
                    } border-t border-current pt-2 sm:pt-3`}
                  >
                    <span className="font-semibold text-sm sm:text-base">
                      Final Price{showAsPricePerPerson ? " (per person)" : ""}
                    </span>
                    <span
                      className={`font-bold text-lg sm:text-xl ${
                        !hasBreakdown
                          ? "text-2xl sm:text-3xl ml-2 sm:ml-3"
                          : "text-lg sm:text-xl"
                      }`}
                    >
                      {currencySymbol}
                      {(totalPrice || 0).toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <div
                  className={`flex items-center ${
                    !hasBreakdown
                      ? "justify-center flex-col gap-1 sm:gap-2"
                      : "justify-between"
                  }`}
                >
                  <span className="font-semibold text-sm sm:text-base">
                    Total Price{showAsPricePerPerson ? " (per person)" : ""}
                  </span>
                  <span
                    className={`font-bold text-lg sm:text-xl ${
                      !hasBreakdown
                        ? "text-2xl sm:text-3xl"
                        : "text-lg sm:text-xl"
                    }`}
                  >
                    {currencySymbol}
                    {(totalPrice || 0).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Information */}
        {(depositRequired ||
          balanceDueDate ||
          (interestFreeInstallmentMonths && depositRequired)) && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Payment Information
            </h3>

            {/* Deposit Information */}
            {depositRequired && (
              <div
                className="p-3 sm:p-4 rounded-xl space-y-1.5 sm:space-y-2 relative"
                style={quoteThemeColorStyles.accentBadge}
              >
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <CreditCard className="w-4 h-4 sm:w-5 sm:h-5" />
                  <h4 className="font-medium text-sm sm:text-base">
                    Deposit Required
                  </h4>
                </div>
                <p className="text-xs sm:text-sm">
                  Secure your booking with a deposit of {currencySymbol}
                  {(depositRequired || 0).toFixed(2)}
                  {showAsPricePerPerson ? " (per person)" : ""}
                </p>
                {depositDueDate && (
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>
                      Deposit due by:{" "}
                      {formatSafeQuoteDate(depositDueDate)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Payment Options Section */}
            {(balanceDueDate ||
              (interestFreeInstallmentMonths && depositRequired)) && (
              <div className="space-y-3 sm:space-y-4">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-600 font-medium">
                    After deposit, choose your payment option:
                  </p>
                </div>

                {/* Option 1: Final Payment */}
                {balanceDueDate && (
                  <div
                    className="p-3 sm:p-4 rounded-xl space-y-1.5 sm:space-y-2 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 transition-colors duration-200"
                    style={
                      {
                        "--hover-border-color": quoteThemeCssVars.primary,
                      } as React.CSSProperties
                    }
                  >
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div
                        className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white text-xs font-bold"
                        style={quoteThemeColorStyles.primaryBg}
                      >
                        1
                      </div>
                      <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                      <h4 className="font-medium text-sm sm:text-base text-gray-900">
                        Final Payment
                      </h4>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Final payment of {currencySymbol}
                      {((totalPrice || 0) - (depositRequired || 0)).toFixed(2)}
                      {showAsPricePerPerson ? " (per person)" : ""} due by{" "}
                      {formatSafeQuoteDate(balanceDueDate)}
                    </p>
                  </div>
                )}

                {/* Option 2: Interest-Free Installments */}
                {interestFreeInstallmentMonths && depositRequired && (
                  <div
                    className="p-3 sm:p-4 rounded-xl space-y-1.5 sm:space-y-2 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 transition-colors duration-200"
                    style={
                      {
                        "--hover-border-color": quoteThemeCssVars.primary,
                      } as React.CSSProperties
                    }
                  >
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <div
                        className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-white text-xs font-bold"
                        style={quoteThemeColorStyles.primaryBg}
                      >
                        2
                      </div>
                      <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />
                      <h4 className="font-medium text-sm sm:text-base text-gray-900">
                        Interest-Free Payment Plan
                      </h4>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">
                      Split your remaining payment into{" "}
                      {interestFreeInstallmentMonths} monthly installments of{" "}
                      {currencySymbol}
                      {(installmentAmount || 0).toFixed(2)}
                      {showAsPricePerPerson ? " (per person)" : ""}
                    </p>
                    <p className="text-xs text-gray-500">
                      0% interest, no extra fees
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
